/**
 * Fiscal-period model shared by the period selector and every page it filters.
 *
 * A company closes its books on the last day of `fiscalYearEndMonth` (1–12,
 * absent = 12 → 31.12). A fiscal year is identified by its CLOSING year, so
 * a June year-end company's "2026" exercise runs 01.07.2025 → 30.06.2026.
 * Semesters, quarters and their labels are fiscal-aligned: Q1 is the first
 * three months of the exercise, not January–March.
 *
 * Every computation goes through `Date.UTC`, which handles month lengths and
 * leap years natively and — unlike date-fns' local-time helpers — cannot drift
 * with the browser timezone. Dates are inclusive ISO `YYYY-MM-DD` strings, the
 * same shape the statement period and the date pickers already speak.
 */

import { format, type Locale } from 'date-fns';
import { enUS } from 'date-fns/locale';

/** Inclusive ISO date range. */
export interface FiscalPeriod {
  from: string;
  to: string;
}

export type PeriodSelection =
  /** Full exercise, identified by its closing year. */
  | { kind: 'fy'; endYear: number }
  | { kind: 'semester'; endYear: number; index: 1 | 2 }
  | { kind: 'quarter'; endYear: number; index: 1 | 2 | 3 | 4 }
  /** Calendar month — the only granularity that is not fiscal-aligned. */
  | { kind: 'month'; year: number; month: number }
  | { kind: 'custom'; from: string; to: string };

export type PeriodGranularity = PeriodSelection['kind'];

export const PERIOD_GRANULARITIES: PeriodGranularity[] = [
  'fy',
  'semester',
  'quarter',
  'month',
  'custom',
];

const MS_PER_DAY = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** 1-based month, overflow-safe: (2025, 14) → { year: 2026, month: 2 }. */
function normalizeMonth(
  year: number,
  month: number,
): { year: number; month: number } {
  const zeroBased = month - 1;
  return {
    year: year + Math.floor(zeroBased / 12),
    month: (((zeroBased % 12) + 12) % 12) + 1,
  };
}

function lastDayOfMonth(year: number, month: number): number {
  // Day 0 of the next month = last day of this one, leap years included.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Anything outside 1–12 (or absent) means a calendar-year company. */
export function normalizeFiscalYearEndMonth(
  endMonth: number | undefined,
): number {
  if (endMonth === undefined) return 12;
  if (!Number.isInteger(endMonth) || endMonth < 1 || endMonth > 12) return 12;
  return endMonth;
}

/** First month of the exercise closing in `endYear`. */
function fiscalYearStartMonth(
  endYear: number,
  endMonth: number,
): { year: number; month: number } {
  return endMonth === 12
    ? { year: endYear, month: 1 }
    : { year: endYear - 1, month: endMonth + 1 };
}

/** Whole calendar month, e.g. (2026, 2) → 2026-02-01 … 2026-02-28. */
export function monthPeriod(year: number, month: number): FiscalPeriod {
  const m = normalizeMonth(year, month);
  return {
    from: isoDate(m.year, m.month, 1),
    to: isoDate(m.year, m.month, lastDayOfMonth(m.year, m.month)),
  };
}

/** Span of `length` months starting `offset` months into the exercise. */
function fiscalSpan(
  endYear: number,
  endMonth: number,
  offset: number,
  length: number,
): FiscalPeriod {
  const start = fiscalYearStartMonth(endYear, endMonth);
  const from = normalizeMonth(start.year, start.month + offset);
  const to = normalizeMonth(start.year, start.month + offset + length - 1);
  return {
    from: isoDate(from.year, from.month, 1),
    to: isoDate(to.year, to.month, lastDayOfMonth(to.year, to.month)),
  };
}

/** Full exercise closing in `endYear`. */
export function fiscalYearPeriod(
  endYear: number,
  endMonth: number,
): FiscalPeriod {
  return fiscalSpan(endYear, normalizeFiscalYearEndMonth(endMonth), 0, 12);
}

export function fiscalSemesterPeriod(
  endYear: number,
  endMonth: number,
  index: 1 | 2,
): FiscalPeriod {
  return fiscalSpan(
    endYear,
    normalizeFiscalYearEndMonth(endMonth),
    (index - 1) * 6,
    6,
  );
}

export function fiscalQuarterPeriod(
  endYear: number,
  endMonth: number,
  index: 1 | 2 | 3 | 4,
): FiscalPeriod {
  return fiscalSpan(
    endYear,
    normalizeFiscalYearEndMonth(endMonth),
    (index - 1) * 3,
    3,
  );
}

export function resolvePeriod(
  selection: PeriodSelection,
  endMonth: number,
): FiscalPeriod {
  switch (selection.kind) {
    case 'fy':
      return fiscalYearPeriod(selection.endYear, endMonth);
    case 'semester':
      return fiscalSemesterPeriod(selection.endYear, endMonth, selection.index);
    case 'quarter':
      return fiscalQuarterPeriod(selection.endYear, endMonth, selection.index);
    case 'month':
      return monthPeriod(selection.year, selection.month);
    case 'custom':
      return { from: selection.from, to: selection.to };
  }
}

/**
 * Closing year of the exercise `today` falls in. A June year-end company is
 * in exercise 2027 from July 2026 on.
 */
export function currentFiscalYear(
  endMonth: number,
  today: Date = new Date(),
): number {
  const month = normalizeFiscalYearEndMonth(endMonth);
  const year = today.getUTCFullYear();
  return today.getUTCMonth() + 1 <= month ? year : year + 1;
}

export function currentFiscalYearSelection(
  endMonth: number,
  today: Date = new Date(),
): PeriodSelection {
  return { kind: 'fy', endYear: currentFiscalYear(endMonth, today) };
}

/** Closing years, newest first — the option list of every granularity. */
export function listRecentFiscalYears(
  endMonth: number,
  count = 6,
  today: Date = new Date(),
): number[] {
  const current = currentFiscalYear(endMonth, today);
  return Array.from({ length: count }, (_, i) => current - i);
}

/** '2025' for a December year-end, '2025/26' otherwise. */
export function fiscalYearLabel(endYear: number, endMonth: number): string {
  if (normalizeFiscalYearEndMonth(endMonth) === 12) return String(endYear);
  return `${endYear - 1}/${pad(endYear % 100)}`;
}

/** The closing year a selection belongs to — drives the FY dropdown value. */
export function selectionFiscalYear(
  selection: PeriodSelection,
  endMonth: number,
  today: Date = new Date(),
): number {
  switch (selection.kind) {
    case 'fy':
    case 'semester':
    case 'quarter':
      return selection.endYear;
    case 'month': {
      const m = normalizeFiscalYearEndMonth(endMonth);
      // A month belongs to the exercise that has not closed before it.
      return selection.month <= m ? selection.year : selection.year + 1;
    }
    case 'custom':
      return currentFiscalYear(endMonth, today);
  }
}

// ── Dates ──────────────────────────────────────────────────────────────────

/** A real calendar day written `YYYY-MM-DD`. */
export function isValidIsoDate(raw: string): boolean {
  if (!ISO_DATE.test(raw)) return false;
  const ts = Date.parse(`${raw}T00:00:00Z`);
  if (Number.isNaN(ts)) return false;
  // Rejects overflow dates like 2026-02-31 that Date.parse normalizes.
  return new Date(ts).toISOString().slice(0, 10) === raw;
}

// ── Filtering ───────────────────────────────────────────────────────────────

/** UTC-midnight ms endpoints, both inclusive — the day-granular convention. */
export function periodToMsRange(period: FiscalPeriod): {
  from: number;
  to: number;
} {
  return {
    from: Date.parse(`${period.from}T00:00:00Z`),
    to: Date.parse(`${period.to}T00:00:00Z`),
  };
}

/**
 * Bounds for a `date >= from && date <= to` filter: the upper end is pushed to
 * the last millisecond of the closing day so timestamps carrying a time
 * component still fall inside.
 */
export function periodToFilterMsRange(period: FiscalPeriod): {
  from: number;
  to: number;
} {
  const range = periodToMsRange(period);
  return { from: range.from, to: range.to + MS_PER_DAY - 1 };
}

/**
 * Inclusive on both ends. The upper bound covers the whole closing day, so a
 * timestamp carrying a time component still counts as inside the period.
 */
export function isWithinPeriod(ms: number, period: FiscalPeriod): boolean {
  const range = periodToFilterMsRange(period);
  return ms >= range.from && ms <= range.to;
}

/** Calendar years the period touches — one or two, never more. */
export function periodYears(period: FiscalPeriod): number[] {
  const first = Number(period.from.slice(0, 4));
  const last = Number(period.to.slice(0, 4));
  const years: number[] = [];
  for (let y = first; y <= last; y += 1) years.push(y);
  return years;
}

/** ISO YYYY-MM-DD → long localized date, e.g. `01 janvier 2026`. */
export function formatPeriodDate(iso: string, locale: Locale = enUS): string {
  const [year, month, day] = iso.split('-').map(Number);
  // Built from parts, not `new Date(iso)`: the bare ISO form parses as UTC
  // midnight and renders the previous day west of Greenwich.
  return format(new Date(year, month - 1, day), 'dd MMMM yyyy', { locale });
}
